<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoneyWithSymbol } from '@/lib/money'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ExternalLink, Globe } from 'lucide-vue-next'
import PriceChangeCell from './PriceChangeCell.vue'
import SideHeading from './SideHeading.vue'
import FilterChips, { type ChipFilter } from '@/components/FilterChips.vue'
import type { CompetitorPrice } from './catalogDetailTypes'

const { t } = useI18n()

/**
 * One entry per side: what the competitors on that side charge. The grade mix used to
 * sit here too; it reads better beside the listings it counts, on the active listings
 * card, and this table wants the full width for its nine columns.
 */
const props = defineProps<{
    sides: { side: 'resell' | 'buyback'; prices: CompetitorPrice[] }[]
    loadingPrices: boolean
}>()

/**
 * A row belongs to exactly one country and one currency, and the two do not imply
 * each other — UK vendors quote resell in GBP and trade-in in EUR — so the filter
 * holds which dimension was picked, not just a code.
 */
const filter = ref<ChipFilter | null>(null)

/** Offered across both sides: a currency present only on one still narrows the page. */
const allPrices = computed(() => props.sides.flatMap((entry) => entry.prices))

const currencies = computed(() => [...new Set(allPrices.value.map((price) => price.currency))].sort())

const countries = computed(() => [...new Set(allPrices.value.map((price) => price.vendor.country))].sort())

const filterGroups = computed(() => [
    { kind: 'currency', label: t('catalog.details.competitorPrices.currency'), options: currencies.value },
    {
        kind: 'country',
        label: t('catalog.details.competitorPrices.country'),
        options: countries.value,
        uppercase: true,
    },
])

const visiblePrices = (prices: CompetitorPrice[]) => {
    const active = filter.value

    if (active === null) {
        return prices
    }

    return prices.filter((price) =>
        active.kind === 'currency' ? price.currency === active.value : price.vendor.country === active.value,
    )
}

</script>

<template>
    <Card>
        <CardHeader>
            <CardTitle class="flex items-center gap-2">
                <Globe class="size-4" />
                {{ t('catalog.details.competitorPrices.title') }}
            </CardTitle>

            <!-- Under the title, because it narrows every section of this card. -->
            <FilterChips v-model="filter" :groups="filterGroups" class="mt-3" />
        </CardHeader>
        <CardContent>
            <div class="space-y-8">
                <div v-for="entry in sides" :key="entry.side">
                    <SideHeading :side="entry.side" class="mb-3" />

                    <div v-if="loadingPrices" class="space-y-3">
                        <Skeleton class="h-8 w-full" />
                        <Skeleton class="h-8 w-full" />
                        <Skeleton class="h-8 w-full" />
                    </div>

                    <p v-else-if="entry.prices.length === 0" class="text-xs text-muted-foreground">
                        {{ t('catalog.details.competitorPrices.empty') }}
                    </p>

                    <!-- Says the selection emptied this side, not that the side has nothing. -->
                    <p v-else-if="visiblePrices(entry.prices).length === 0" class="text-xs text-muted-foreground">
                        {{ t('catalog.details.competitorPrices.emptyForFilter', { filter: filter?.value }) }}
                    </p>

                    <!--
                        No market column: the section heading already says which side these
                        are. Fixed layout so the resell table and the trade-in table below
                        it share one grid — with auto layout each sizes its columns from its
                        own vendor names and the two read as unrelated tables.
                    -->
                    <Table v-else class="table-fixed">
                        <TableHeader>
                            <TableRow>
                                <TableHead class="w-[18%]">{{
                                    t('catalog.details.competitorPrices.vendor')
                                }}</TableHead>
                                <TableHead class="w-[8%]">{{
                                    t('catalog.details.competitorPrices.country')
                                }}</TableHead>
                                <TableHead class="text-right">{{
                                    t('catalog.details.competitorPrices.min')
                                }}</TableHead>
                                <TableHead class="text-right">{{
                                    t('catalog.details.competitorPrices.max')
                                }}</TableHead>
                                <TableHead class="text-right">{{
                                    t('catalog.details.competitorPrices.trendAvg')
                                }}</TableHead>
                                <TableHead class="text-right">{{
                                    t('catalog.details.competitorPrices.30d')
                                }}</TableHead>
                                <TableHead class="text-right">{{
                                    t('catalog.details.competitorPrices.60d')
                                }}</TableHead>
                                <TableHead class="text-right">{{
                                    t('catalog.details.competitorPrices.lastBuybox')
                                }}</TableHead>
                                <TableHead class="text-right">{{
                                    t('catalog.details.competitorPrices.dailyWins')
                                }}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow v-for="(cp, idx) in visiblePrices(entry.prices)" :key="idx">
                                <TableCell class="truncate font-medium">{{ cp.vendor.name }}</TableCell>
                                <TableCell class="uppercase">{{ cp.vendor.country }}</TableCell>
                                <TableCell class="text-right tabular-nums">{{
                                    formatMoneyWithSymbol(cp.min_price, cp.currency_symbol)
                                }}</TableCell>
                                <TableCell class="text-right tabular-nums">{{
                                    formatMoneyWithSymbol(cp.max_price, cp.currency_symbol)
                                }}</TableCell>
                                <TableCell class="text-right tabular-nums">{{
                                    formatMoneyWithSymbol(cp.trend_avg_price, cp.currency_symbol)
                                }}</TableCell>
                                <TableCell class="text-right">
                                    <PriceChangeCell :value="cp.last30_days_price_change" />
                                </TableCell>
                                <TableCell class="text-right">
                                    <PriceChangeCell :value="cp.last60_days_price_change" />
                                </TableCell>
                                <TableCell class="text-right">
                                    <template v-if="cp.buybox?.last">
                                        <a
                                            :href="cp.buybox.last.url"
                                            target="_blank"
                                            class="inline-flex items-center gap-1 tabular-nums text-sm hover:text-foreground transition-colors"
                                        >
                                            {{ formatMoneyWithSymbol(cp.buybox.last.price, cp.buybox.currency_symbol) }}
                                            <ExternalLink class="size-3" />
                                        </a>
                                    </template>
                                    <span v-else class="text-muted-foreground">—</span>
                                </TableCell>
                                <TableCell class="text-right tabular-nums">
                                    <span v-if="cp.buybox?.win_rate !== null && cp.buybox?.win_rate !== undefined"
                                        >{{ cp.buybox.win_rate }}%</span
                                    >
                                    <span v-else class="text-muted-foreground">—</span>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>
            </div>
        </CardContent>
    </Card>
</template>
